import { useCallback, useMemo, useRef, useState } from 'react';

import { ListPagination, Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { Table } from 'src/common/components/Table';
import { OrbitIcons } from 'src/common/constants.';
import { useQuery } from 'src/common/hooks/useQuery';
import { scalingToCampaign } from 'src/tcr/api';
import { AccountProps, ParentAccount } from 'src/tcr/types';

import { ScalingModal } from './ScalingModal';

interface ParentAccountListProps {
  query?: string;
}

export const ParentAccountList = ({ query = '' }: ParentAccountListProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1);
  const scalingModalRef = useRef<ModalRefProps | null>(null);
  const { data, isLoading } = useQuery(
    `/parent_account_paginated_admin?page=${currentPage}&name=${query}`
  );
  const parents = data?.results || [];
  const totalCount = data?.count;

  const handleScaling = useCallback((row, brand_id) => {
    scalingModalRef.current?.show(row, brand_id);
  }, []);

  const columns = useMemo(
    () => [
      { title: 'Name', index: 'name' },
      {
        title: 'TCR Ready',
        index: 'tcr_ready',
        renderer: (row: AccountProps) =>
          row.tcr_ready ? (
            <Icon name={OrbitIcons.Check} className="text-secondary-0.0" />
          ) : (
            <Icon name={OrbitIcons.X} className="text-red-1.0" />
          ),
      },
      {
        title: 'Brand',
        index: 'brand',
        renderer: (row: ParentAccount) => {
          if (row.brands)
            return row.brands.map((brand) => (
              <div className="flex items-center py-2" key={brand.brand_id}>
                {brand.brand_id}
                {brand.brand_id === row.tcr_brand?.brand_id && (
                  <button
                    className="ml-auto btn bg-white-2.0"
                    onClick={() => handleScaling(row, brand.id)}
                  >
                    <Icon name={OrbitIcons.Scaling} />
                  </button>
                )}
              </div>
            ));
          return '';
        },
      },
    ],
    [handleScaling]
  );

  const handleSave = async (
    parent_account: ParentAccount,
    brand_id: string,
    use_case: string
  ): Promise<void> => {
    try {
      await scalingToCampaign({ parent_account: parent_account.id, use_case, brand_id });
      Toast.success('Parent account has been scaling successfully.');
    } catch (error) {
      Toast.error('Error occurred while scaling parent account.');
    }
  };

  return (
    <>
      <div className="scrollbar-thin flex-1 overflow-auto">
        <Table<ParentAccount> data={parents} columns={columns} loading={isLoading} />
      </div>
      {totalCount > 20 && (
        <ListPagination
          currentPage={currentPage}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
        />
      )}
      <ScalingModal ref={scalingModalRef} onSave={handleSave} />
    </>
  );
};
